import React, { useState, useRef, useEffect } from 'react';

const AIStoryCoWriterGame = ({ gameData, onComplete, user }) => {
    const [storyParts, setStoryParts] = useState([]);
    const [userInput, setUserInput] = useState('');
    const [isAIThinking, setIsAIThinking] = useState(false);
    const [turn, setTurn] = useState('user'); 
    const [showStarters, setShowStarters] = useState(true);
    const storyEndRef = useRef(null);

    const scrollToBottom = () => {
        storyEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [storyParts, isAIThinking]);

    const handleStarterClick = (starter) => {
        setUserInput(starter);
        setShowStarters(false);
    };

    const handleSubmit = async () => {
        if (!userInput.trim()) return;

        const newStoryParts = [...storyParts, userInput];
        setStoryParts(newStoryParts);
        setUserInput('');
        setTurn('ai');
        setIsAIThinking(true);
        if (navigator.vibrate) navigator.vibrate(50);

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/v1/ai-games/story-cowriter/continue`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                    },
                    body: JSON.stringify({
                        story_parts: storyParts,
                        user_input: userInput,
                        age: user?.age,
                        grade: user?.grade
                    })
                }
            );

            const data = await response.json();

            if (data.success) {
                setStoryParts([...newStoryParts, data.continuation]);
                setTurn('user');
                if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
            }
        } catch (error) {
            console.error('Error continuing story:', error);
            const fallbacks = [
                "ir nutiko kažkas nuostabaus!",
                "staiga pasirodė magiškas padaras.",
                "ir prasidėjo neįtikėtinas nuotykis!"
            ];
            const fallback = fallbacks[Math.floor(Math.random() * fallbacks.length)];
            setStoryParts([...newStoryParts, fallback]);
            setTurn('user');
        } finally {
            setIsAIThinking(false);
        }
    };

    const handleFinish = () => {
        const finalScore = storyParts.length * 10;
        onComplete?.(finalScore);
    };

    if (!gameData) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-rose-100">
                <h3 className="text-base md:text-3xl font-black text-rose-600 mb-4 tracking-tight">Klaida</h3>
                <p className="text-slate-500 font-bold text-lg">Nepavyko užkrauti žaidimo duomenų.</p>
            </div>
        );
    }

    return (
        <div className="space-y-3 md:space-y-8 animate-slide-up select-none max-w-5xl mx-auto">
            {/* Header / Progress Area */}
            <div className="premium-glass rounded-lg md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full blur-[80px] pointer-events-none"></div>
                <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center text-base md:text-3xl shadow-inner animate-float">
                            ✍️
                        </div>
                        <div>
                            <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight">
                                AI Pasakų Rašytojas
                            </h3>
                            <p className="text-slate-500 font-bold">
                                Kurk pasaką kartu su dirbtiniu intelektu!
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="bg-white/50 px-6 py-3 rounded-2xl border border-white/50 shadow-inner">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Dalių</p>
                            <p className="text-2xl font-black text-indigo-600 leading-none">{storyParts.length}</p>
                        </div>
                        <div className="bg-white/50 px-6 py-3 rounded-2xl border border-white/50 shadow-inner text-center min-w-[100px]">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Taškai</p>
                            <p className="text-2xl font-black text-slate-800 leading-none">⭐ {storyParts.length * 10}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Story Canvas */}
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-3 md:p-8 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative flex flex-col h-[600px] overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(99,102,241,0.03)_0%,transparent_70%)] pointer-events-none" />
                
                <div className="flex-1 overflow-y-auto pr-4 space-y-3 md:space-y-8 pb-10 scrollbar-hide">
                    {storyParts.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-center space-y-6 opacity-40">
                            <div className="text-xl md:text-5xl md:text-[120px] animate-float">📝</div>
                            <div>
                                <p className="text-base md:text-3xl font-black text-slate-800">Tavo lapas dar tuščias...</p>
                                <p className="text-xl font-bold text-slate-500">Pradėk rašyti pirmąjį sakinį!</p>
                            </div>
                        </div>
                    ) : (
                        storyParts.map((part, index) => {
                            const isUser = index % 2 === 0;
                            return (
                                <div
                                    key={index}
                                    className={`flex w-full ${isUser ? 'justify-end' : 'justify-start'} animate-pop-in`}
                                >
                                    <div className={`
                                        max-w-[85%] rounded-xl md:rounded-[3rem] p-3 md:p-8 shadow-2xl border-2 relative
                                        ${isUser 
                                            ? 'bg-gradient-to-br from-indigo-600 to-indigo-800 text-white border-indigo-400 rounded-tr-none' 
                                            : 'bg-white text-slate-800 border-slate-100 rounded-tl-none'}
                                    `}>
                                        <div className={`absolute top-0 ${isUser ? '-right-4' : '-left-4'} w-10 h-10 flex items-center justify-center text-base md:text-3xl`}>
                                            {isUser ? '👤' : '🤖'}
                                        </div>
                                        <p className="text-2xl font-bold leading-relaxed font-serif">
                                            {part}
                                        </p>
                                    </div>
                                </div>
                            );
                        })
                    )}

                    {isAIThinking && (
                        <div className="flex justify-start animate-pulse">
                            <div className="bg-white/80 rounded-xl md:rounded-[3rem] rounded-tl-none p-3 md:p-8 shadow-xl border-2 border-slate-50 flex items-center gap-4">
                                <div className="text-base md:text-3xl">🤖</div>
                                <div className="flex gap-2">
                                    <div className="w-3 h-3 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
                                    <div className="w-3 h-3 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                                    <div className="w-3 h-3 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
                                </div>
                                <span className="text-lg font-black text-slate-400 uppercase tracking-widest ml-2">AI galvoja...</span>
                            </div>
                        </div>
                    )}
                    <div ref={storyEndRef} />
                </div>

                {/* Input Controls */}
                <div className="pt-8 border-t-2 border-slate-100/50 mt-auto relative z-10">
                    {turn === 'user' && !isAIThinking ? (
                        <div className="space-y-6">
                            {showStarters && storyParts.length === 0 && (
                                <div className="flex flex-wrap gap-3 justify-center">
                                    {gameData.starter_prompts?.map((starter, index) => (
                                        <button
                                            key={index}
                                            onClick={() => handleStarterClick(starter)}
                                            className="px-6 py-3 bg-white/80 hover:bg-white text-indigo-600 rounded-2xl text-lg font-black shadow-lg hover:scale-105 active:scale-95 transition-all border-2 border-indigo-50"
                                        >
                                            {starter}
                                        </button>
                                    ))}
                                </div>
                            )}

                            <div className="relative group">
                                <textarea
                                    value={userInput}
                                    onChange={(e) => setUserInput(e.target.value)}
                                    placeholder="Tęsk pasaką čia..."
                                    rows={3}
                                    className="w-full p-3 md:p-8 text-2xl font-bold bg-white rounded-lg md:rounded-[2.5rem] border-4 border-slate-100 focus:border-indigo-500 focus:outline-none shadow-2xl transition-all placeholder:text-slate-200 resize-none font-serif"
                                />
                                <div className="absolute right-6 bottom-6 flex gap-3">
                                    {storyParts.length >= 4 && (
                                        <button
                                            onClick={handleFinish}
                                            className="px-8 py-4 bg-emerald-500 text-white rounded-2xl font-black shadow-lg hover:bg-emerald-600 transition-all active:scale-95 border-b-4 border-emerald-700"
                                        >
                                            ✅ BAIGTI
                                        </button>
                                    )}
                                    <button
                                        onClick={handleSubmit}
                                        disabled={!userInput.trim()}
                                        className="px-8 py-4 bg-indigo-600 text-white rounded-2xl font-black shadow-lg hover:bg-indigo-700 transition-all active:scale-95 border-b-4 border-indigo-900 flex items-center gap-2 disabled:opacity-40"
                                    >
                                        <span>✨ TĘSTI</span>
                                        <span className="text-xl">🚀</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-slate-50/50 rounded-lg md:rounded-[2.5rem] p-4 md:p-10 text-center border-2 border-dashed border-slate-200">
                            <p className="text-2xl font-black text-slate-300 uppercase tracking-widest italic">
                                {isAIThinking ? 'Palauk, AI rašo savo dalį...' : 'Tavo eilė ruošiasi...'}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AIStoryCoWriterGame;
